import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Spin,
  Result,
  Space,
  Select,
  message,
  Tag
} from 'antd';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  PrinterOutlined,
  EditOutlined,
  GlobalOutlined,
  VerticalAlignTopOutlined
} from '@ant-design/icons';
import axios from 'axios';
import AuthService from '../utils/auth';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { addCanvasToPdf } from '../utils/canvasPdf';
import MarkdownContent from '../components/MarkdownContent';

const { Option } = Select;

// 格式化日期：2026-01-19T00:00:00.000Z -> 2026-01-19
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return dateStr.split('T')[0];
};

// 计算两个日期之间的天数差
const calcNights = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return '-';
  const d1 = new Date(checkIn);
  const d2 = new Date(checkOut);
  const diff = Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : '-';
};

const TourNoticePreview = () => {
  const { tourId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [noticeData, setNoticeData] = useState(null);
  const [tourists, setTourists] = useState([]);
  const [selectedTouristId, setSelectedTouristId] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [addOnNoticeTemplates, setAddOnNoticeTemplates] = useState([]);
  const [language, setLanguage] = useState('zh'); // 'zh' for Chinese, 'en' for English
  const [activeSection, setActiveSection] = useState('');
  const [showBackTop, setShowBackTop] = useState(false);
  const contentRef = useRef(null);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

  useEffect(() => {
    fetchTourists();
  }, [tourId]);

  useEffect(() => {
    fetchPreviewData();
  }, [tourId, selectedTouristId]);

  const fetchTourists = async () => {
    try {
      const response = await axios.get(
        `${API_BASE}/tourists?tourId=${tourId}`,
        { headers: AuthService.getAuthHeaders() }
      );
      if (response.data.success) {
        setTourists(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch tourists:', error);
    }
  };

  const fetchPreviewData = async () => {
    setLoading(true);
    try {
      let url = `${API_BASE}/tour-notices/tour/${tourId}/preview`;
      if (selectedTouristId) {
        url += `?touristId=${selectedTouristId}`;
      }
      const response = await axios.get(url, {
        headers: AuthService.getAuthHeaders()
      });
      if (response.data.success) {
        setNoticeData(response.data.data);
      }

      // 如果选择了游客，获取其Add-On通知书模板
      if (selectedTouristId) {
        fetchAddOnNoticeTemplates(selectedTouristId);
      } else {
        setAddOnNoticeTemplates([]);
      }
    } catch (error) {
      message.error('加载预览失败');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAddOnNoticeTemplates = async (touristId) => {
    try {
      const response = await axios.get(
        `${API_BASE}/addon-notice-templates/tourist/${touristId}`,
        { headers: AuthService.getAuthHeaders() }
      );
      if (response.data.success) {
        setAddOnNoticeTemplates(response.data.data || []);
      }
    } catch (error) {
      console.error('获取Add-On通知书模板失败:', error);
      setAddOnNoticeTemplates([]);
    }
  };

  // 构建当前可见的分类列表
  const buildSections = () => {
    if (!noticeData) return [];
    const { notice, touristHotels, touristAddOns, destination, templates, attachments, touristDocuments } = noticeData;
    const hasHotelPdf = touristDocuments?.some(d => d.documentType === 'hotel');
    const sections = [];

    if (language === 'zh') {
      if (notice?.flightInfo && (notice.flightInfo.outbound?.length > 0 || notice.flightInfo.inbound?.length > 0)) {
        sections.push({ id: 'section-flight', label: '航班信息' });
      }
      if (touristHotels?.length > 0 || notice?.hotelInfo?.length > 0 || hasHotelPdf) {
        sections.push({ id: 'section-hotel', label: '酒店信息' });
      }
      if (touristAddOns?.length > 0) {
        sections.push({ id: 'section-addons', label: '自费项目' });
      }
      if (destination?.departureGuide) {
        sections.push({ id: 'section-departure', label: '出境指南' });
      }
      if (destination?.entryGuide) {
        sections.push({ id: 'section-entry', label: '入境指南' });
      }
      if (destination?.customsInfo) {
        sections.push({ id: 'section-customs', label: '海关申报' });
      }
      if (destination?.vaccineInfo) {
        sections.push({ id: 'section-vaccine', label: '疫苗说明' });
      }
      if (destination?.localNotices) {
        sections.push({ id: 'section-local', label: '注意事项' });
      }
      if (destination?.insuranceInfo) {
        sections.push({ id: 'section-insurance', label: '旅游保险' });
      }
      if (templates?.length > 0) {
        templates.forEach((t, i) => {
          sections.push({ id: `section-template-${i}`, label: t.title?.length > 6 ? t.title.slice(0, 6) + '...' : t.title });
        });
      }
      if (notice?.otherNotes) {
        sections.push({ id: 'section-other', label: '其他注意' });
      }
      if (attachments?.length > 0) {
        sections.push({ id: 'section-attachments', label: '附件清单' });
      }
      if (touristDocuments?.length > 0) {
        sections.push({ id: 'section-documents', label: '预订文档' });
      }
    } else {
      if (notice?.flightInfo && (notice.flightInfo.outbound?.length > 0 || notice.flightInfo.inbound?.length > 0)) {
        sections.push({ id: 'section-flight', label: 'Flights' });
      }
      if (touristHotels?.length > 0 || notice?.hotelInfo?.length > 0 || hasHotelPdf) {
        sections.push({ id: 'section-hotel', label: 'Hotels' });
      }
      if (touristAddOns?.length > 0) {
        sections.push({ id: 'section-addons', label: 'Add-ons' });
      }
      if (destination?.departureGuideEn) {
        sections.push({ id: 'section-departure', label: 'Departure Guide' });
      }
      if (destination?.entryGuideEn) {
        sections.push({ id: 'section-entry', label: 'Entry Guide' });
      }
      if (destination?.customsInfoEn) {
        sections.push({ id: 'section-customs', label: 'Customs' });
      }
      if (destination?.vaccineInfoEn) {
        sections.push({ id: 'section-vaccine', label: 'Vaccine' });
      }
      if (destination?.localNoticesEn) {
        sections.push({ id: 'section-local', label: 'Local Notes' });
      }
      if (destination?.insuranceInfoEn) {
        sections.push({ id: 'section-insurance', label: 'Insurance' });
      }
      if (templates?.length > 0) {
        templates.forEach((t, i) => {
          const label = t.titleEn || t.title;
          sections.push({ id: `section-template-${i}`, label: label?.length > 10 ? label.slice(0, 10) + '...' : label });
        });
      }
      if (notice?.otherNotesEn || notice?.otherNotes) {
        sections.push({ id: 'section-other', label: 'Other Notes' });
      }
      if (attachments?.length > 0) {
        sections.push({ id: 'section-attachments', label: 'Attachments' });
      }
      if (touristDocuments?.length > 0) {
        sections.push({ id: 'section-documents', label: 'Documents' });
      }
    }
    return sections;
  };

  const sections = buildSections();

  // 滚动监听：高亮当前区块标签 + 显示回到顶部按钮
  useEffect(() => {
    const handleScroll = () => {
      setShowBackTop(window.scrollY > 400);

      const sectionIds = sections.map(s => s.id);
      if (sectionIds.length === 0) return;

      let current = '';
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120) {
            current = id;
          }
        }
      }

      // 页面滚动到底部时，高亮最后一个区块
      const atBottom = (window.innerHeight + window.scrollY) >= (document.body.scrollHeight - 50);
      if (atBottom && sectionIds.length > 0) {
        current = sectionIds[sectionIds.length - 1];
      }

      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections.length, language]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top, behavior: 'smooth' });
      setActiveSection(id);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDownloadPDF = async () => {
    if (!contentRef.current) return;

    setDownloading(true);
    try {
      const content = contentRef.current;

      const canvas = await html2canvas(content, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const pdf = new jsPDF('p', 'mm', 'a4');
      addCanvasToPdf(pdf, canvas, { margin: 5 });

      const touristName = noticeData?.tourist?.name || 'preview';
      const fileName = `出团通知书_${touristName}.pdf`;
      pdf.save(fileName);
      message.success(`已开始下载：${fileName}`);
    } catch (error) {
      message.error('PDF生成失败');
      console.error(error);
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!noticeData) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Result
          status="404"
          title="暂无数据"
          subTitle="无法加载通知书预览"
          extra={<Button type="primary" onClick={() => navigate(-1)}>返回</Button>}
        />
      </div>
    );
  }

  const { tourist, tour, notice, templates, attachments, touristHotels, touristAddOns, destination, touristDocuments, guide, localGuides } = noticeData;
  // 只上传了酒店 PDF、未分配结构化酒店时，酒店信息栏显示"详情见PDF附件"
  const hasHotelPdf = touristDocuments?.some(d => d.documentType === 'hotel');

  // 中文数字序号
  const chineseNumbers = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];

  // 通用样式
  const styles = {
    page: {
      width: '210mm',
      minHeight: '297mm',
      margin: '110px auto 24px',
      background: '#fff',
      padding: '15mm 20mm',
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      fontSize: '11pt',
      lineHeight: 1.8,
      color: '#333'
    },
    chineseFont: {
      fontFamily: '"Microsoft YaHei", "微软雅黑", "PingFang SC", "Helvetica Neue", sans-serif'
    },
    englishFont: {
      fontFamily: '"Times New Roman", "Georgia", serif'
    },
    sectionTitle: {
      fontSize: '13pt',
      borderBottom: '2px solid #1a5490',
      paddingBottom: '5px',
      marginBottom: '15px',
      color: '#1a5490',
      fontWeight: 'bold'
    },
    tableStyle: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '10pt'
    },
    thStyle: {
      border: '1px solid #ddd',
      padding: '8px',
      textAlign: 'left',
      background: '#f0f5fa'
    },
    tdStyle: {
      border: '1px solid #ddd',
      padding: '8px'
    }
  };

  return (
    <div style={{ background: '#e8e8e8', minHeight: '100vh' }}>
      {/* 工具栏 */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          background: '#1a1a2e',
          padding: '12px 24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
        className="no-print"
      >
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            返回
          </Button>
          <span style={{ color: '#fff', fontWeight: 'bold' }}>预览：{tour?.productName}</span>
        </Space>
        <Space>
          <Select
            placeholder="选择游客预览"
            allowClear
            style={{ width: 200 }}
            value={selectedTouristId}
            onChange={setSelectedTouristId}
          >
            {tourists.map(t => (
              <Option key={t.id} value={t.id}>{t.touristName}</Option>
            ))}
          </Select>
          {/* 语言切换按钮 */}
          <Button.Group>
            <Button
              type={language === 'zh' ? 'primary' : 'default'}
              onClick={() => setLanguage('zh')}
              icon={<GlobalOutlined />}
            >
              中文
            </Button>
            <Button
              type={language === 'en' ? 'primary' : 'default'}
              onClick={() => setLanguage('en')}
            >
              English
            </Button>
          </Button.Group>
          <Button
            icon={<EditOutlined />}
            onClick={() => navigate(`/admin/tours/${tourId}/notice`)}
          >
            编辑
          </Button>
          <Button icon={<PrinterOutlined />} onClick={handlePrint}>
            {language === 'zh' ? '打印' : 'Print'}
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownloadPDF}
            loading={downloading}
          >
            {language === 'zh' ? '下载PDF' : 'Download PDF'}
          </Button>
        </Space>
      </div>

      {/* 分类标签导航栏 */}
      {sections.length > 0 && (
        <div
          style={{
            position: 'fixed',
            top: '52px',
            left: 0,
            right: 0,
            background: '#fff',
            padding: '8px 24px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
            zIndex: 999,
            display: 'flex',
            gap: '6px',
            overflowX: 'auto',
            flexWrap: 'nowrap',
            WebkitOverflowScrolling: 'touch',
          }}
          className="no-print"
        >
          {sections.map((sec) => (
            <Tag
              key={sec.id}
              onClick={() => scrollToSection(sec.id)}
              color={activeSection === sec.id ? 'blue' : 'default'}
              style={{
                cursor: 'pointer',
                fontSize: '12px',
                padding: '4px 12px',
                margin: 0,
                flexShrink: 0,
                borderRadius: '16px',
                fontWeight: activeSection === sec.id ? 'bold' : 'normal',
                transition: 'all 0.3s',
              }}
            >
              {sec.label}
            </Tag>
          ))}
        </div>
      )}

      {/* 一键回到顶部按钮 */}
      {showBackTop && (
        <div
          onClick={scrollToTop}
          className="no-print"
          style={{
            position: 'fixed',
            bottom: '40px',
            right: '40px',
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            background: '#1a5490',
            color: '#fff',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            zIndex: 1001,
            transition: 'opacity 0.3s',
            fontSize: '20px',
          }}
          title={language === 'zh' ? '回到顶部' : 'Back to Top'}
        >
          <VerticalAlignTopOutlined />
        </div>
      )}

      {/* 通知书内容 */}
      <div ref={contentRef}>

        {/* ==================== 中文版本 ==================== */}
        {language === 'zh' && (
        <div style={{ ...styles.page, ...styles.chineseFont }}>
          {/* 中文页眉 */}
          <div style={{
            borderBottom: '3px double #1a5490',
            paddingBottom: '10px',
            marginBottom: '20px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <h1 style={{
                fontSize: '26pt',
                fontWeight: 'bold',
                color: '#1a5490',
                margin: '0 0 5px 0',
                letterSpacing: '10px'
              }}>
                出团通知书
              </h1>
              <p style={{
                fontSize: '11pt',
                color: '#666',
                margin: 0
              }}>
                万国旅行社 Worldway Travel
              </p>
            </div>
          </div>

          {/* 中文基本信息 */}
          <div style={{
            border: '1px solid #1a5490',
            padding: '15px 20px',
            marginBottom: '25px',
            background: '#f8fafc'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '5px 0', width: '30%', verticalAlign: 'top' }}>
                    <strong>游客姓名：</strong><br/>
                    <span style={{ wordBreak: 'break-word' }}>{tourist?.passportName || tourist?.name || '待定'}</span>
                  </td>
                  <td style={{ padding: '5px 0', width: '20%', verticalAlign: 'top' }}>
                    <strong>护照号码：</strong><br/>
                    {tourist?.passportNumber || '-'}
                  </td>
                  <td style={{ padding: '5px 0', width: '28%', verticalAlign: 'top' }}>
                    <strong>产品名称：</strong><br/>
                    {tour?.productName}
                  </td>
                  <td style={{ padding: '5px 0', width: '22%', verticalAlign: 'top' }}>
                    <strong>出发日期：</strong><br/>
                    {formatDate(tour?.departureDate)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* 中文 - 领队与导游信息 */}
          <div id="section-guide" style={{ marginBottom: '25px' }}>
            <h2 style={styles.sectionTitle}>领队与导游信息</h2>
            {guide && (
              <div style={{ marginBottom: (localGuides && localGuides.length > 0) ? '12px' : 0 }}>
                <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>领队</div>
                <div style={{ background: '#e6f7ff', padding: '10px 12px', border: '1px solid #91d5ff', borderRadius: '4px' }}>
                  <table style={{ width: '100%' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '3px 0', width: '33%' }}><strong>姓名：</strong>{guide.name}</td>
                        <td style={{ padding: '3px 0', width: '33%' }}><strong>电话：</strong>{guide.phone || '-'}</td>
                        <td style={{ padding: '3px 0', width: '33%' }}><strong>微信：</strong>{guide.wechat || '-'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {localGuides && localGuides.length > 0 && (
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>导游</div>
                {localGuides.map((lg, idx) => (
                  <div key={idx} style={{ background: '#f6ffed', padding: '10px 12px', border: '1px solid #b7eb8f', borderRadius: '4px', marginBottom: idx < localGuides.length - 1 ? '8px' : 0 }}>
                    <table style={{ width: '100%' }}>
                      <tbody>
                        <tr>
                          <td style={{ padding: '3px 0', width: '33%' }}><strong>姓名：</strong>{lg.name}</td>
                          <td style={{ padding: '3px 0', width: '33%' }}><strong>电话：</strong>{lg.phone || '-'}</td>
                          <td style={{ padding: '3px 0', width: '33%' }}><strong>备注：</strong>{lg.note || '-'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
            {!guide && (
              <div style={{ background: '#fff7e6', padding: '10px 12px', border: '1px solid #ffd591', borderRadius: '4px' }}>
                本产品无领队，请自行值机
              </div>
            )}
          </div>

          {/* 中文 - 航班信息 */}
          {notice?.flightInfo && (notice.flightInfo.outbound?.length > 0 || notice.flightInfo.inbound?.length > 0) && (
            <div id="section-flight" style={{ marginBottom: '25px' }}>
              <h2 style={styles.sectionTitle}>
                机场及航班信息
              </h2>
              <p style={{ marginBottom: '10px' }}>
                机票详情请查看附件。使用护照原件自行值机。
              </p>

              <table style={styles.tableStyle}>
                <thead>
                  <tr>
                    <th style={styles.thStyle}>出发日期</th>
                    <th style={styles.thStyle}>航线</th>
                    <th style={styles.thStyle}>航班号</th>
                    <th style={styles.thStyle}>起飞时间</th>
                    <th style={styles.thStyle}>到达时间</th>
                    <th style={styles.thStyle}>行李额</th>
                  </tr>
                </thead>
                <tbody>
                  {[...(notice.flightInfo.outbound || []), ...(notice.flightInfo.inbound || [])].map((flight, index) => {
                    let arrivalDisplay = flight.arrivalTime || '';
                    if (flight.arrivalDate && flight.date && flight.arrivalDate !== flight.date) {
                      arrivalDisplay = `${flight.arrivalTime} (+1天)`;
                    }
                    return (
                      <tr key={index}>
                        <td style={styles.tdStyle}>{flight.date}</td>
                        <td style={styles.tdStyle}>{flight.from} → {flight.to}</td>
                        <td style={styles.tdStyle}>{flight.flight}</td>
                        <td style={styles.tdStyle}>{flight.time}</td>
                        <td style={styles.tdStyle}>{arrivalDisplay}</td>
                        <td style={styles.tdStyle}>{flight.baggage || flight.note || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>


              {notice.flightInfo.baggageNote && (
                <p style={{ background: '#fff8e6', padding: '10px', border: '1px solid #ffd666', fontSize: '10pt' }}>
                  <strong>行李说明：</strong>{notice.flightInfo.baggageNote}
                </p>
              )}
              {notice.flightInfo.additionalNote && (
                <p style={{ background: '#fff1f0', padding: '10px', border: '1px solid #ffa39e', fontSize: '10pt', marginTop: '10px' }}>
                  <strong>特别注意：</strong>{notice.flightInfo.additionalNote}
                </p>
              )}
            </div>
          )}

          {/* 中文 - 酒店信息 */}
          {(touristHotels?.length > 0 || notice?.hotelInfo?.length > 0 || hasHotelPdf) && (
            <div id="section-hotel" style={{ marginBottom: '25px' }}>
              <h2 style={styles.sectionTitle}>
                酒店信息
              </h2>

              {touristHotels?.length > 0 ? (
                <>
                  <table style={styles.tableStyle}>
                    <thead>
                      <tr>
                        <th style={styles.thStyle}>入住日期</th>
                        <th style={{ ...styles.thStyle, textAlign: 'center' }}>天数</th>
                        <th style={styles.thStyle}>酒店名称</th>
                        <th style={{ ...styles.thStyle, textAlign: 'center' }}>星级</th>
                        <th style={{ ...styles.thStyle, textAlign: 'center' }}>含早</th>
                        <th style={styles.thStyle}>电话</th>
                      </tr>
                    </thead>
                    <tbody>
                      {touristHotels.map((th, index) => (
                        <tr key={th.id || index}>
                          <td style={{ ...styles.tdStyle, whiteSpace: 'nowrap' }}>{th.checkInDate}</td>
                          <td style={{ ...styles.tdStyle, textAlign: 'center' }}>{calcNights(th.checkInDate, th.checkOutDate)}</td>
                          <td style={styles.tdStyle}>
                            {th.hotel?.name}
                            {th.hotel?.address && (
                              <div style={{ fontSize: '8pt', color: '#999', marginTop: '2px' }}>{th.hotel.address}</div>
                            )}
                          </td>
                          <td style={{ ...styles.tdStyle, textAlign: 'center', color: '#faad14' }}>
                            {th.hotel?.starLevel ? '★'.repeat(th.hotel.starLevel) : '-'}
                          </td>
                          <td style={{ ...styles.tdStyle, textAlign: 'center' }}>
                            {th.hotel?.hasBreakfast ? '含早' : '不含'}
                          </td>
                          <td style={{ ...styles.tdStyle, whiteSpace: 'nowrap' }}>{th.hotel?.phone || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* 酒店注意事项 */}
                  {touristHotels.some(th => th.hotel?.notes) && (
                    <div style={{ marginTop: '15px', padding: '10px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: '4px' }}>
                      <strong style={{ color: '#d48806' }}>酒店注意事项：</strong>
                      <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px' }}>
                        {touristHotels.filter(th => th.hotel?.notes).map((th, index) => (
                          <li key={index} style={{ marginBottom: '5px' }}>
                            <strong>{th.hotel.name}：</strong>{th.hotel.notes}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : notice?.hotelInfo?.length > 0 ? (
                <table style={styles.tableStyle}>
                  <thead>
                    <tr>
                      <th style={styles.thStyle}>城市</th>
                      <th style={styles.thStyle}>酒店名称</th>
                      <th style={styles.thStyle}>地址</th>
                      <th style={styles.thStyle}>电话</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notice.hotelInfo.map((hotel, index) => (
                      <tr key={index}>
                        <td style={styles.tdStyle}>{hotel.city}</td>
                        <td style={styles.tdStyle}>{hotel.name}</td>
                        <td style={{ ...styles.tdStyle, fontSize: '9pt' }}>{hotel.address}</td>
                        <td style={styles.tdStyle}>{hotel.phone || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : hasHotelPdf && (
                <div style={{ padding: '12px 15px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '4px', fontSize: '10pt', color: '#389e0d' }}>
                  酒店详情见PDF附件（见下方"预订文档"）
                </div>
              )}
            </div>
          )}

          {/* 中文 - 自费项目 */}
          {touristAddOns?.length > 0 && (
            <div id="section-addons" style={{ marginBottom: '25px' }}>
              <h2 style={styles.sectionTitle}>
                自费项目
              </h2>
              {touristAddOns.map((ta, index) => (
                <div key={ta.id || index} style={{
                  border: '1px solid #e8e8e8',
                  padding: '12px',
                  marginBottom: '10px',
                  background: '#fafafa'
                }}>
                  <p style={{ margin: '0 0 8px 0' }}>
                    <strong style={{ color: '#1a5490' }}>
                      第{ta.addOnProduct?.dayNumber}天：{ta.addOnProduct?.name}
                    </strong>
                  </p>
                  {ta.addOnProduct?.meetingTime && (
                    <p style={{ margin: '5px 0', fontSize: '10pt' }}>
                      <strong>集合时间：</strong>{ta.addOnProduct.meetingTime}
                      {ta.addOnProduct?.duration && ` | 时长：${ta.addOnProduct.duration}`}
                    </p>
                  )}
                  {ta.addOnProduct?.meetingPlace && (
                    <p style={{ margin: '5px 0', fontSize: '10pt' }}>
                      <strong>集合地点：</strong>{ta.addOnProduct.meetingPlace}
                    </p>
                  )}
                  {ta.addOnProduct?.summary && (
                    <p style={{ margin: '5px 0', fontSize: '10pt', color: '#666' }}>
                      {ta.addOnProduct.summary}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* 中文 - 目的地信息 */}
          {destination && (destination.departureGuide || destination.entryGuide || destination.customsInfo || destination.vaccineInfo || destination.localNotices || destination.insuranceInfo) && (
            <>
              {destination.departureGuide && (
                <div id="section-departure" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>
                    出境指南
                  </h2>
                  <MarkdownContent text={destination.departureGuide} />
                </div>
              )}

              {destination.entryGuide && (
                <div id="section-entry" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>
                    入境指南
                  </h2>
                  <MarkdownContent text={destination.entryGuide} />
                </div>
              )}

              {destination.customsInfo && (
                <div id="section-customs" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>
                    海关申报说明
                  </h2>
                  <MarkdownContent text={destination.customsInfo} />
                </div>
              )}

              {destination.vaccineInfo && (
                <div id="section-vaccine" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>
                    疫苗说明
                  </h2>
                  <MarkdownContent text={destination.vaccineInfo} />
                </div>
              )}

              {destination.localNotices && (
                <div id="section-local" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>
                    注意事项
                  </h2>
                  <MarkdownContent text={destination.localNotices} />
                </div>
              )}

              {destination.insuranceInfo && (
                <div id="section-insurance" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>
                    旅游保险说明
                  </h2>
                  <MarkdownContent text={destination.insuranceInfo} />
                </div>
              )}
            </>
          )}

          {/* 中文 - 通用模板内容 */}
          {templates?.map((template, index) => {
            return (
              <div key={template.id} id={`section-template-${index}`} style={{ marginBottom: '25px' }}>
                <h2 style={styles.sectionTitle}>
                  {template.title}
                </h2>
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {template.content}
                </div>
              </div>
            );
          })}

          {/* 中文 - 其他注意事项 */}
          {notice?.otherNotes && (
            <div id="section-other" style={{ marginBottom: '25px' }}>
              <h2 style={styles.sectionTitle}>其他注意事项</h2>
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {notice.otherNotes}
              </div>
            </div>
          )}

          {/* 中文 - 附件清单 */}
          {attachments?.length > 0 && (
            <div id="section-attachments" style={{ marginBottom: '25px' }}>
              <h2 style={styles.sectionTitle}>附件清单</h2>
              {attachments.map((att) => (
                <div key={att.id} style={{ marginBottom: '20px' }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1a5490' }}>
                    {att.name}
                    {att.description && <span style={{ fontWeight: 'normal', color: '#666' }}> - {att.description}</span>}
                  </p>
                  {att.fileType === 'image' && (att.signedUrl || att.filePath) && (
                    <img
                      src={att.signedUrl || att.filePath}
                      alt={att.name}
                      style={{
                        maxWidth: '100%',
                        height: 'auto',
                        border: '1px solid #e8e8e8',
                        borderRadius: '4px'
                      }}
                    />
                  )}
                  {att.fileType === 'pdf' && (att.signedUrl || att.filePath) && (
                    <a
                      href={att.signedUrl || att.filePath}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1677ff' }}
                    >
                      点击查看PDF文件
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* 中文 - 您的预订文档 */}
          {touristDocuments?.length > 0 && (
            <div id="section-documents" style={{ marginBottom: '25px' }}>
              <h2 style={styles.sectionTitle}>您的预订文档</h2>
              <div style={{
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: '4px',
                padding: '15px',
                marginBottom: '10px'
              }}>
                <p style={{ margin: '0 0 10px 0', color: '#52c41a', fontWeight: 'bold' }}>
                  请点击下载您的预订确认文件：
                </p>
                {/* 酒店 */}
                {touristDocuments.filter(d => d.documentType === 'hotel').length > 0 && (
                  <div style={{ marginBottom: '10px' }}>
                    <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>【酒店】</p>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      {touristDocuments.filter(d => d.documentType === 'hotel').map(doc => (
                        <li key={doc.id} style={{ marginBottom: '5px' }}>
                          <a
                            href={doc.signedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1677ff' }}
                          >
                            {doc.documentName}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* 电子机票 */}
                {touristDocuments.filter(d => d.documentType === 'flight').length > 0 && (
                  <div style={{ marginBottom: '10px' }}>
                    <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>【电子机票】</p>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      {touristDocuments.filter(d => d.documentType === 'flight').map(doc => (
                        <li key={doc.id} style={{ marginBottom: '5px' }}>
                          <a
                            href={doc.signedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1677ff' }}
                          >
                            {doc.documentName}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* 自费项目文档 */}
                {touristDocuments.filter(d => d.documentType === 'addon').length > 0 && (
                  <div style={{ marginBottom: '10px' }}>
                    <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>【附加项目文档】</p>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      {touristDocuments.filter(d => d.documentType === 'addon').map(doc => (
                        <li key={doc.id} style={{ marginBottom: '5px' }}>
                          <a
                            href={doc.signedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1677ff' }}
                          >
                            {doc.documentName}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 中文页脚 */}
          <div style={{
            marginTop: '40px',
            paddingTop: '20px',
            borderTop: '3px double #1a5490',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '14pt', fontWeight: 'bold', color: '#1a5490', marginBottom: '15px' }}>
              祝您旅途愉快！
            </p>
            <div style={{ fontSize: '10pt', color: '#666', lineHeight: 1.6 }}>
              <p style={{ margin: '5px 0', fontWeight: 'bold', color: '#1a5490' }}>
                万国旅行社 Worldway Travel
              </p>
              <p style={{ margin: '5px 0' }}>
                地址：14C/155 Queen Street Auckland Central, Auckland, New Zealand
              </p>
              <p style={{ margin: '5px 0' }}>
                电话：0800 116 688 | 邮箱：confirmation@worldway.co.nz
              </p>
            </div>
          </div>
        </div>
        )}

        {/* ==================== 英文版本 ==================== */}
        {language === 'en' && (
        <div style={{ ...styles.page, ...styles.englishFont }}>
          {/* English Header */}
          <div style={{
            borderBottom: '3px double #1a5490',
            paddingBottom: '10px',
            marginBottom: '20px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <h1 style={{
                fontSize: '24pt',
                fontWeight: 'bold',
                color: '#1a5490',
                margin: '0 0 5px 0',
                letterSpacing: '3px'
              }}>
                GROUP DEPARTURE NOTICE
              </h1>
              <p style={{
                fontSize: '11pt',
                color: '#666',
                margin: 0
              }}>
                Worldway Travel
              </p>
            </div>
          </div>

          {/* English Basic Info */}
          <div style={{
            border: '1px solid #1a5490',
            padding: '15px 20px',
            marginBottom: '25px',
            background: '#f8fafc'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '5px 0', width: '25%' }}>
                    <strong>Guest Name:</strong><br/>
                    {tourist?.passportName || tourist?.name || 'TBA'}
                  </td>
                  <td style={{ padding: '5px 0', width: '25%' }}>
                    <strong>Passport No.:</strong><br/>
                    {tourist?.passportNumber || '-'}
                  </td>
                  <td style={{ padding: '5px 0', width: '25%' }}>
                    <strong>Product:</strong><br/>
                    {tour?.productName}
                  </td>
                  <td style={{ padding: '5px 0', width: '25%' }}>
                    <strong>Departure Date:</strong><br/>
                    {formatDate(tour?.departureDate)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* English - Leader & Guide Information */}
          <div id="section-guide" style={{ marginBottom: '25px' }}>
            <h2 style={styles.sectionTitle}>Leader & Guide Information</h2>
            {guide && (
              <div style={{ marginBottom: (localGuides && localGuides.length > 0) ? '12px' : 0 }}>
                <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>Leader</div>
                <div style={{ background: '#e6f7ff', padding: '10px 12px', border: '1px solid #91d5ff', borderRadius: '4px' }}>
                  <table style={{ width: '100%' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '3px 0', width: '33%' }}><strong>Name: </strong>{guide.name}</td>
                        <td style={{ padding: '3px 0', width: '33%' }}><strong>Phone: </strong>{guide.phone || '-'}</td>
                        <td style={{ padding: '3px 0', width: '33%' }}><strong>WeChat: </strong>{guide.wechat || '-'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {localGuides && localGuides.length > 0 && (
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>Guide</div>
                {localGuides.map((lg, idx) => (
                  <div key={idx} style={{ background: '#f6ffed', padding: '10px 12px', border: '1px solid #b7eb8f', borderRadius: '4px', marginBottom: idx < localGuides.length - 1 ? '8px' : 0 }}>
                    <table style={{ width: '100%' }}>
                      <tbody>
                        <tr>
                          <td style={{ padding: '3px 0', width: '33%' }}><strong>Name: </strong>{lg.name}</td>
                          <td style={{ padding: '3px 0', width: '33%' }}><strong>Phone: </strong>{lg.phone || '-'}</td>
                          <td style={{ padding: '3px 0', width: '33%' }}><strong>Note: </strong>{lg.note || '-'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
            {!guide && (
              <div style={{ background: '#fff7e6', padding: '10px 12px', border: '1px solid #ffd591', borderRadius: '4px' }}>
                No tour leader for this product, please check in by yourself
              </div>
            )}
          </div>

          {/* English - Flight Information */}
          {notice?.flightInfo && (notice.flightInfo.outbound?.length > 0 || notice.flightInfo.inbound?.length > 0) && (
            <div id="section-flight" style={{ marginBottom: '25px' }}>
              <h2 style={styles.sectionTitle}>Airport & Flight Information</h2>
              <p style={{ marginBottom: '10px' }}>
                Please check the attached ticket details. No tour leader for this product.
                Please arrive at the airport 3 hours early and check in with your passport.
              </p>

              <table style={styles.tableStyle}>
                <thead>
                  <tr>
                    <th style={styles.thStyle}>Date</th>
                    <th style={styles.thStyle}>Route</th>
                    <th style={styles.thStyle}>Flight</th>
                    <th style={styles.thStyle}>Departure</th>
                    <th style={styles.thStyle}>Arrival</th>
                    <th style={styles.thStyle}>Baggage</th>
                  </tr>
                </thead>
                <tbody>
                  {[...(notice.flightInfo.outbound || []), ...(notice.flightInfo.inbound || [])].map((flight, index) => {
                    let arrivalDisplay = flight.arrivalTime || '';
                    if (flight.arrivalDate && flight.date && flight.arrivalDate !== flight.date) {
                      arrivalDisplay = `${flight.arrivalTime} (+1 Day)`;
                    }
                    return (
                      <tr key={index}>
                        <td style={styles.tdStyle}>{flight.date}</td>
                        <td style={styles.tdStyle}>{flight.from} → {flight.to}</td>
                        <td style={styles.tdStyle}>{flight.flight}</td>
                        <td style={styles.tdStyle}>{flight.time}</td>
                        <td style={styles.tdStyle}>{arrivalDisplay}</td>
                        <td style={styles.tdStyle}>{flight.baggage || flight.note || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {notice.flightInfo.baggageNote && (
                <p style={{ background: '#fff8e6', padding: '10px', border: '1px solid #ffd666', fontSize: '10pt' }}>
                  <strong>Baggage Allowance:</strong> {notice.flightInfo.baggageNote}
                </p>
              )}
              {notice.flightInfo.additionalNote && (
                <p style={{ background: '#fff1f0', padding: '10px', border: '1px solid #ffa39e', fontSize: '10pt', marginTop: '10px' }}>
                  <strong>Important Notice:</strong> {notice.flightInfo.additionalNote}
                </p>
              )}
            </div>
          )}

          {/* English - Hotel Information */}
          {(touristHotels?.length > 0 || notice?.hotelInfo?.length > 0 || hasHotelPdf) && (
            <div id="section-hotel" style={{ marginBottom: '25px' }}>
              <h2 style={styles.sectionTitle}>Hotel Information</h2>

              {touristHotels?.length > 0 ? (
                <>
                  <table style={styles.tableStyle}>
                    <thead>
                      <tr>
                        <th style={styles.thStyle}>Check-in</th>
                        <th style={{ ...styles.thStyle, textAlign: 'center' }}>Nights</th>
                        <th style={styles.thStyle}>Hotel</th>
                        <th style={{ ...styles.thStyle, textAlign: 'center' }}>Stars</th>
                        <th style={{ ...styles.thStyle, textAlign: 'center' }}>Breakfast</th>
                        <th style={styles.thStyle}>Phone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {touristHotels.map((th, index) => (
                        <tr key={th.id || index}>
                          <td style={{ ...styles.tdStyle, whiteSpace: 'nowrap' }}>{th.checkInDate}</td>
                          <td style={{ ...styles.tdStyle, textAlign: 'center' }}>{calcNights(th.checkInDate, th.checkOutDate)}</td>
                          <td style={styles.tdStyle}>
                            {th.hotel?.nameEn}
                            {(th.hotel?.addressEn || th.hotel?.address) && (
                              <div style={{ fontSize: '8pt', color: '#999', marginTop: '2px' }}>{th.hotel.addressEn || th.hotel.address}</div>
                            )}
                          </td>
                          <td style={{ ...styles.tdStyle, textAlign: 'center', color: '#faad14' }}>
                            {th.hotel?.starLevel ? '★'.repeat(th.hotel.starLevel) : '-'}
                          </td>
                          <td style={{ ...styles.tdStyle, textAlign: 'center' }}>
                            {th.hotel?.hasBreakfast ? 'Yes' : 'No'}
                          </td>
                          <td style={{ ...styles.tdStyle, whiteSpace: 'nowrap' }}>{th.hotel?.phone || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* Hotel Notes */}
                  {touristHotels.some(th => th.hotel?.notesEn || th.hotel?.notes) && (
                    <div style={{ marginTop: '15px', padding: '10px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: '4px' }}>
                      <strong style={{ color: '#d48806' }}>Hotel Notes:</strong>
                      <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px' }}>
                        {touristHotels.filter(th => th.hotel?.notesEn || th.hotel?.notes).map((th, index) => (
                          <li key={index} style={{ marginBottom: '5px' }}>
                            <strong>{th.hotel.nameEn || th.hotel.name}:</strong> {th.hotel.notesEn || th.hotel.notes}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : notice?.hotelInfo?.length > 0 ? (
                <table style={styles.tableStyle}>
                  <thead>
                    <tr>
                      <th style={styles.thStyle}>City</th>
                      <th style={styles.thStyle}>Hotel Name</th>
                      <th style={styles.thStyle}>Address</th>
                      <th style={styles.thStyle}>Phone</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notice.hotelInfo.map((hotel, index) => (
                      <tr key={index}>
                        <td style={styles.tdStyle}>{hotel.city}</td>
                        <td style={styles.tdStyle}>{hotel.name}</td>
                        <td style={{ ...styles.tdStyle, fontSize: '9pt' }}>{hotel.address}</td>
                        <td style={styles.tdStyle}>{hotel.phone || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : hasHotelPdf && (
                <div style={{ padding: '12px 15px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '4px', fontSize: '10pt', color: '#389e0d' }}>
                  Please refer to the PDF attachment for hotel details (see "Booking Documents" below).
                </div>
              )}
            </div>
          )}

          {/* English - Optional Add-ons */}
          {touristAddOns?.length > 0 && (
            <div id="section-addons" style={{ marginBottom: '25px' }}>
              <h2 style={styles.sectionTitle}>Optional Add-ons</h2>
              {touristAddOns.map((ta, index) => (
                <div key={ta.id || index} style={{
                  border: '1px solid #e8e8e8',
                  padding: '12px',
                  marginBottom: '10px',
                  background: '#fafafa'
                }}>
                  <p style={{ margin: '0 0 8px 0' }}>
                    <strong style={{ color: '#1a5490' }}>
                      Day {ta.addOnProduct?.dayNumber}: {ta.addOnProduct?.nameEn || ta.addOnProduct?.name}
                    </strong>
                  </p>
                  {ta.addOnProduct?.meetingTime && (
                    <p style={{ margin: '5px 0', fontSize: '10pt' }}>
                      <strong>Meeting Time:</strong> {ta.addOnProduct.meetingTime}
                      {ta.addOnProduct?.duration && ` | Duration: ${ta.addOnProduct.duration}`}
                    </p>
                  )}
                  {(ta.addOnProduct?.meetingPlaceEn || ta.addOnProduct?.meetingPlace) && (
                    <p style={{ margin: '5px 0', fontSize: '10pt' }}>
                      <strong>Meeting Place:</strong> {ta.addOnProduct.meetingPlaceEn || ta.addOnProduct.meetingPlace}
                    </p>
                  )}
                  {(ta.addOnProduct?.summaryEn || ta.addOnProduct?.summary) && (
                    <p style={{ margin: '5px 0', fontSize: '10pt', color: '#666' }}>
                      {ta.addOnProduct.summaryEn || ta.addOnProduct.summary}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* English - Destination Information */}
          {destination && (destination.departureGuideEn || destination.entryGuideEn || destination.customsInfoEn || destination.vaccineInfoEn || destination.localNoticesEn || destination.insuranceInfoEn) && (
            <>
              {destination.departureGuideEn && (
                <div id="section-departure" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>Departure Guide</h2>
                  <MarkdownContent text={destination.departureGuideEn} />
                </div>
              )}

              {destination.entryGuideEn && (
                <div id="section-entry" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>Entry Guide</h2>
                  <MarkdownContent text={destination.entryGuideEn} />
                </div>
              )}

              {destination.customsInfoEn && (
                <div id="section-customs" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>Customs Declaration</h2>
                  <MarkdownContent text={destination.customsInfoEn} />
                </div>
              )}

              {destination.vaccineInfoEn && (
                <div id="section-vaccine" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>Vaccine Requirements</h2>
                  <MarkdownContent text={destination.vaccineInfoEn} />
                </div>
              )}

              {destination.localNoticesEn && (
                <div id="section-local" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>Local Notices</h2>
                  <MarkdownContent text={destination.localNoticesEn} />
                </div>
              )}

              {destination.insuranceInfoEn && (
                <div id="section-insurance" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>Travel Insurance</h2>
                  <MarkdownContent text={destination.insuranceInfoEn} />
                </div>
              )}
            </>
          )}

          {/* English - Template Content */}
          {templates?.map((template, index) => {
            return (
              <div key={template.id} id={`section-template-${index}`} style={{ marginBottom: '25px' }}>
                <h2 style={styles.sectionTitle}>
                  {template.titleEn || template.title}
                </h2>
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {template.contentEn || template.content}
                </div>
              </div>
            );
          })}

          {/* English - Other Notes */}
          {(notice?.otherNotesEn || notice?.otherNotes) && (
            <div id="section-other" style={{ marginBottom: '25px' }}>
              <h2 style={styles.sectionTitle}>Other Important Notes</h2>
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {notice.otherNotesEn || notice.otherNotes}
              </div>
            </div>
          )}

          {/* English - Attachments */}
          {attachments?.length > 0 && (
            <div id="section-attachments" style={{ marginBottom: '25px' }}>
              <h2 style={styles.sectionTitle}>Attachments</h2>
              {attachments.map((att) => (
                <div key={att.id} style={{ marginBottom: '20px' }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1a5490' }}>
                    {att.nameEn || att.name}
                    {att.description && <span style={{ fontWeight: 'normal', color: '#666' }}> - {att.description}</span>}
                  </p>
                  {att.fileType === 'image' && (att.signedUrl || att.filePath) && (
                    <img
                      src={att.signedUrl || att.filePath}
                      alt={att.nameEn || att.name}
                      style={{
                        maxWidth: '100%',
                        height: 'auto',
                        border: '1px solid #e8e8e8',
                        borderRadius: '4px'
                      }}
                    />
                  )}
                  {att.fileType === 'pdf' && (att.signedUrl || att.filePath) && (
                    <a
                      href={att.signedUrl || att.filePath}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1677ff' }}
                    >
                      Click to view PDF file
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* English - Your Booking Documents */}
          {touristDocuments?.length > 0 && (
            <div id="section-documents" style={{ marginBottom: '25px' }}>
              <h2 style={styles.sectionTitle}>Your Booking Documents</h2>
              <div style={{
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: '4px',
                padding: '15px',
                marginBottom: '10px'
              }}>
                <p style={{ margin: '0 0 10px 0', color: '#52c41a', fontWeight: 'bold' }}>
                  Please click to download your booking confirmations:
                </p>
                {/* Hotel Confirmations */}
                {touristDocuments.filter(d => d.documentType === 'hotel').length > 0 && (
                  <div style={{ marginBottom: '10px' }}>
                    <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>【Hotel Confirmations】</p>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      {touristDocuments.filter(d => d.documentType === 'hotel').map(doc => (
                        <li key={doc.id} style={{ marginBottom: '5px' }}>
                          <a
                            href={doc.signedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1677ff' }}
                          >
                            {doc.documentName}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* Flight Tickets */}
                {touristDocuments.filter(d => d.documentType === 'flight').length > 0 && (
                  <div style={{ marginBottom: '10px' }}>
                    <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>【Flight Tickets】</p>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      {touristDocuments.filter(d => d.documentType === 'flight').map(doc => (
                        <li key={doc.id} style={{ marginBottom: '5px' }}>
                          <a
                            href={doc.signedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1677ff' }}
                          >
                            {doc.documentName}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* Add-on Documents */}
                {touristDocuments.filter(d => d.documentType === 'addon').length > 0 && (
                  <div style={{ marginBottom: '10px' }}>
                    <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>【Add-on Documents】</p>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      {touristDocuments.filter(d => d.documentType === 'addon').map(doc => (
                        <li key={doc.id} style={{ marginBottom: '5px' }}>
                          <a
                            href={doc.signedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1677ff' }}
                          >
                            {doc.documentName}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Add-On Notice Templates */}
          {addOnNoticeTemplates?.length > 0 && (
            <div style={{ marginTop: '50px' }}>
              <div style={{ borderTop: '3px double #e65c00', paddingTop: '30px' }}>
                <h1 style={{
                  fontSize: '18pt',
                  fontWeight: 'bold',
                  color: '#e65c00',
                  textAlign: 'center',
                  marginBottom: '30px'
                }}>
                  Add-On Activities Notice / 自费项目通知书
                </h1>

                {addOnNoticeTemplates.map((template, idx) => (
                  <div key={template.id} style={{
                    marginBottom: '40px',
                    paddingBottom: '30px',
                    borderBottom: idx < addOnNoticeTemplates.length - 1 ? '1px dashed #ccc' : 'none'
                  }}>
                    {/* Add-On Product Name */}
                    <h2 style={{
                      fontSize: '14pt',
                      fontWeight: 'bold',
                      color: '#e65c00',
                      marginBottom: '20px',
                      padding: '10px',
                      backgroundColor: '#fff5eb',
                      borderLeft: '4px solid #e65c00'
                    }}>
                      {template.addOnProduct?.name || 'Add-On Activity'}
                      {template.addOnProduct?.nameEn && (
                        <span style={{ fontWeight: 'normal', color: '#666', marginLeft: '10px' }}>
                          / {template.addOnProduct.nameEn}
                        </span>
                      )}
                    </h2>

                    {/* Meeting Info */}
                    {(template.meetingTime || template.meetingPlace) && (
                      <div style={{ marginBottom: '15px' }}>
                        <h3 style={{ fontSize: '12pt', fontWeight: 'bold', color: '#333', marginBottom: '8px' }}>
                          集合信息 / Meeting Information
                        </h3>
                        {template.meetingTime && (
                          <p style={{ margin: '5px 0' }}>
                            <strong>集合时间 / Meeting Time:</strong> {template.meetingTime}
                          </p>
                        )}
                        {template.meetingPlace && (
                          <p style={{ margin: '5px 0' }}>
                            <strong>集合地点 / Meeting Place:</strong><br />
                            {template.meetingPlace}
                            {template.meetingPlaceEn && (
                              <span style={{ color: '#666' }}><br />{template.meetingPlaceEn}</span>
                            )}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Flight Info */}
                    {template.hasFlightInfo && template.flightDetails && (
                      <div style={{ marginBottom: '15px' }}>
                        <h3 style={{ fontSize: '12pt', fontWeight: 'bold', color: '#333', marginBottom: '8px' }}>
                          航班信息 / Flight Information
                        </h3>
                        <div style={{ whiteSpace: 'pre-wrap' }}>{template.flightDetails}</div>
                        {template.flightDetailsEn && (
                          <div style={{ color: '#666', marginTop: '5px', whiteSpace: 'pre-wrap' }}>
                            {template.flightDetailsEn}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Itinerary */}
                    {template.itinerary && (
                      <div style={{ marginBottom: '15px' }}>
                        <h3 style={{ fontSize: '12pt', fontWeight: 'bold', color: '#333', marginBottom: '8px' }}>
                          行程安排 / Itinerary
                        </h3>
                        <div style={{ whiteSpace: 'pre-wrap' }}>{template.itinerary}</div>
                        {template.itineraryEn && (
                          <div style={{ color: '#666', marginTop: '5px', whiteSpace: 'pre-wrap' }}>
                            {template.itineraryEn}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Notes */}
                    {template.notes && (
                      <div style={{ marginBottom: '15px' }}>
                        <h3 style={{ fontSize: '12pt', fontWeight: 'bold', color: '#333', marginBottom: '8px' }}>
                          注意事项 / Notes
                        </h3>
                        <div style={{ whiteSpace: 'pre-wrap' }}>{template.notes}</div>
                        {template.notesEn && (
                          <div style={{ color: '#666', marginTop: '5px', whiteSpace: 'pre-wrap' }}>
                            {template.notesEn}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Attachments with Download Links */}
                    {template.attachments?.length > 0 && (
                      <div style={{ marginBottom: '15px' }}>
                        <h3 style={{ fontSize: '12pt', fontWeight: 'bold', color: '#333', marginBottom: '8px' }}>
                          附件下载 / Attachments
                        </h3>
                        <ul style={{ paddingLeft: '20px' }}>
                          {template.attachments.filter(att => att.isDownloadable).map((att) => {
                            const fileUrl = att.fileUrl?.startsWith('http')
                              ? att.fileUrl
                              : `${API_BASE.replace('/api', '')}/${att.fileUrl}`;
                            return (
                              <li key={att.id} style={{ marginBottom: '8px' }}>
                                <a
                                  href={fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: '#1890ff',
                                    textDecoration: 'underline',
                                    cursor: 'pointer'
                                  }}
                                >
                                  {att.name}
                                  {att.nameEn && <span style={{ color: '#666' }}> / {att.nameEn}</span>}
                                </a>
                                <Tag color={
                                  att.attachmentType === 'ticket' ? 'blue' :
                                  att.attachmentType === 'hotel' ? 'green' :
                                  att.attachmentType === 'itinerary' ? 'purple' : 'default'
                                } style={{ marginLeft: '8px' }}>
                                  {att.attachmentType === 'ticket' ? '机票' :
                                   att.attachmentType === 'hotel' ? '酒店' :
                                   att.attachmentType === 'itinerary' ? '行程单' : '其他'}
                                </Tag>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* English Footer */}
          <div style={{
            marginTop: '40px',
            paddingTop: '20px',
            borderTop: '3px double #1a5490',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '14pt', fontWeight: 'bold', color: '#1a5490', marginBottom: '15px' }}>
              Have a Pleasant Journey!
            </p>
            <div style={{ fontSize: '10pt', color: '#666', lineHeight: 1.6 }}>
              <p style={{ margin: '5px 0', fontWeight: 'bold', color: '#1a5490' }}>
                Worldway Travel
              </p>
              <p style={{ margin: '5px 0' }}>
                Address: 14C/155 Queen Street Auckland Central, Auckland, New Zealand
              </p>
              <p style={{ margin: '5px 0' }}>
                Tel: 0800 116 688 | Email: confirmation@worldway.co.nz
              </p>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* 打印样式 + 标签导航滚动条隐藏 */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          @page {
            size: A4;
            margin: 10mm;
          }
        }
      `}</style>
    </div>
  );
};

export default TourNoticePreview;
